/**
 * useResearchRun - the ONE live-state source for a research run.
 *
 * Supersedes `useResearchStream` (still used by the legacy chat surface until
 * that path is removed). Everything the new surface knows about an in-flight
 * query comes from here: the hub subscription, the correlation-id filter,
 * reconnection, the reduction of `research.*` events into a clean state
 * object, and the completion side-effects. Nothing else subscribes.
 *
 * Hardening, because this is the part that was flaky:
 *
 *  • The hub listener is a WILDCARD one, so the correlation-id filter is
 *    load-bearing - without it a second query's events drive this one's UI.
 *  • `connectToHub` is retried with backoff. A failed connect used to leave
 *    the run silently dead until the blind timeout; now it retries and, if it
 *    truly cannot connect, says so.
 *  • Attach is idempotent and detaches on unmount - a stale handler writing
 *    into a dead reducer was the old leak.
 *  • Events after `done` are ignored, so a duplicate `completed` cannot
 *    re-open the run or re-fire the reconcile.
 *  • `research.completed` builds the envelope through the SAME adapter as the
 *    blob/history path: one parse in the whole app.
 *  • The reconcile (`researchConfirmCompletion`) is mandatory - the engine has
 *    no network path to the backend, so this call is what persists the message
 *    metadata and the generated title.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAppDispatch } from '../../store';
import {
  researchConfirmCompletion,
  researchGetMessageContent,
  researchListChatGroups,
} from '../../store/actions/ChatAction';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { toResearchResponse } from './adapter';
import { ActivityStage, closeStages, failStages, reduceStage, stageKeyOf } from './activity';
import {
  InterimMessage,
  addInterim,
  cuesFromBlob,
  cuesFromPayload,
  interimSequence,
  interimFromBlob,
  interimText,
  isInterimEvent,
  isProgressCueEvent,
  reconcileInterim,
} from './interim';
import {
  DraftReview,
  IntentSource,
  ResearchIntent,
  ResearchResponse,
  VerificationReport,
  isResearchIntent,
} from './types';
import { toErrorText } from '../../utils/errorMessage';
import logger from '../../utils/logger';

export type RunPhase = 'idle' | 'connecting' | 'running' | 'completed' | 'failed';

/** Events that end a run and carry its result. Never treated as anything else. */
const TERMINAL_EVENTS = new Set(['research.completed', 'research.failed']);

export interface ResearchRunState {
  phase: RunPhase;
  /** Every stage the pipeline reported, in arrival order. Never pre-drawn. */
  stages: ActivityStage[];
  intent: ResearchIntent | null;
  intentSource: IntentSource | null;
  response: ResearchResponse | null;
  /** R5 groundedness annotation from the completed event. */
  verification: VerificationReport | null;
  /** R6 draft self-review. */
  draftReview: DraftReview | null;
  done: boolean;
  error: string | null;
  chatTitle: string | null;
  /** True only when the completed event flagged the title as generated. */
  chatTitleGenerated: boolean;
  retrievalData: Record<string, any> | null;
  /** The engine's conversational lines during the run, in order. */
  interim: InterimMessage[];
  /**
   * Tailored progress phrases for THIS question, sent once early on.
   *
   * Not narration - the working indicator's vocabulary while the run is in
   * flight. Empty until the engine sends them, and on a fast path it never
   * does, so nothing may depend on them arriving.
   */
  cues: string[];
  /**
   * The run this state belongs to.
   *
   * Without it a consumer cannot tell "no events yet for the new run" from
   * "the finished previous run": the reducer is reset in an effect, so for
   * one render after a new correlation id arrives the hook still returns the
   * PREVIOUS run's state - response and all. That single render is how a
   * follow-up turn adopted the earlier turn's document.
   */
  correlationId: string | null;
}

const INITIAL: ResearchRunState = {
  correlationId: null,
  interim: [],
  cues: [],
  phase: 'idle',
  stages: [],
  intent: null,
  intentSource: null,
  response: null,
  verification: null,
  draftReview: null,
  done: false,
  error: null,
  chatTitle: null,
  chatTitleGenerated: false,
  retrievalData: null,
};

/* ------------------------------------------------------------------ */
/*  Per-intent timeout budgets                                         */
/* ------------------------------------------------------------------ */

const MINUTE = 60 * 1000;

/**
 * The intent is known from second one (`research.intent` fires before any
 * retrieval), so a bullet list no longer waits the same blind ten minutes as
 * a deep research run.
 */
export const INTENT_TIMEOUT_MS: Record<ResearchIntent, number> = {
  deep_research: 15 * MINUTE,
  memo: 10 * MINUTE,
  draft: 10 * MINUTE,
  chronology: 6 * MINUTE,
  overview: 5 * MINUTE,
  bullets: 4 * MINUTE,
  find_document: 4 * MINUTE,
  qa: 4 * MINUTE,
};

export const DEFAULT_TIMEOUT_MS = 10 * MINUTE;

export function timeoutForIntent(intent: ResearchIntent | null): number {
  return intent ? INTENT_TIMEOUT_MS[intent] : DEFAULT_TIMEOUT_MS;
}

/** Connect retry schedule: short, then give up and say so. */
export const CONNECT_RETRY_MS = [500, 1500, 4000];

/* ------------------------------------------------------------------ */
/*  Reducer                                                            */
/* ------------------------------------------------------------------ */

type Action =
  | { type: 'reset'; correlationId?: string | null }
  | { type: 'connecting' }
  | { type: 'connect-failed'; message: string }
  | {
      type: 'event';
      eventName: string;
      data: any;
      explicitIntent?: ResearchIntent | null;
      messageUuid?: string;
      correlationId?: string;
      now: number;
    }
  | { type: 'retrieval-data'; retrieval: Record<string, any> }
  | { type: 'timeout'; message: string };

export function runReducer(state: ResearchRunState, action: Action): ResearchRunState {
  switch (action.type) {
    case 'reset':
      return { ...INITIAL, correlationId: action.correlationId ?? null };

    case 'connecting':
      return state.phase === 'idle' ? { ...state, phase: 'connecting' } : state;

    case 'connect-failed':
      if (state.done) return state;
      return { ...state, phase: 'failed', done: true, error: action.message };

    case 'retrieval-data':
      return { ...state, retrievalData: action.retrieval };

    case 'timeout':
      if (state.done) return state;
      return {
        ...state,
        phase: 'failed',
        done: true,
        error: action.message,
        stages: failStages(state.stages, action.message),
      };

    case 'event':
      break;

    default:
      return state;
  }

  // A finished run ignores everything else - a duplicate `completed` must not
  // re-open it or re-fire the reconcile.
  if (state.done) return state;

  const { eventName, data, now } = action;
  const payload = data?.data || {};
  const message = String(payload.message || data?.message || '');
  const key = stageKeyOf(eventName);

  const advance = (patch: Partial<ResearchRunState> = {}): ResearchRunState => ({
    ...state,
    phase: 'running',
    stages: reduceStage(state.stages, key, payload, message, now),
    ...patch,
  });

  /*
    THE LOADER'S CUES, from wherever they turn up.

    The engine sends them on `research.interim` with `stage: "progress_cues"`,
    an empty `text` and `sequence: 0` - a vocabulary, not a sentence. This is
    checked on EVERY research event rather than only on the interim one: the
    payload is unmistakable (a `cues` array), the client cannot re-ask for a
    list it missed, and a run that gets them one event later than expected is
    a run whose loader speaks. A cue payload is never narration and never a
    stage, whatever event carried it.

    EXCEPT on a terminal event. `research.completed` carries the whole result
    blob, `progress_cues` included - taking an early return there would drop
    the answer on the floor. The terminal events read their cues below, where
    the rest of the blob is read.
  */
  if (!TERMINAL_EVENTS.has(eventName) && isProgressCueEvent(payload)) {
    const cues = cuesFromPayload(payload);
    return cues.length ? { ...state, phase: 'running', cues } : state;
  }

  // Interim lines are conversation, not a pipeline stage - they accumulate
  // in order rather than replacing one another.
  if (isInterimEvent(eventName)) {
    const text = interimText(payload, message);
    if (!text) return state;
    return {
      ...state,
      phase: 'running',
      interim: addInterim(
        state.interim,
        text,
        interimSequence(payload, state.interim.length + 1),
        payload?.stage
      ),
    };
  }

  switch (eventName) {
    case 'research.started':
      return advance();

    case 'research.intent': {
      // Fires before any retrieval - this commits the right skeleton.
      const raw = payload.intent;
      const intent = isResearchIntent(raw) ? raw : state.intent;
      const rawSource = payload.intent_source;
      const intentSource: IntentSource =
        action.explicitIntent && action.explicitIntent === intent
          ? 'explicit'
          : rawSource === 'explicit'
          ? 'explicit'
          : 'server';
      return advance({ intent, intentSource });
    }

    case 'research.orchestration': {
      const title = payload.chat_title;
      const usable =
        typeof title === 'string' && title.length > 3 && title !== 'Orchestrator (Placeholder)'
          ? title
          : state.chatTitle;
      return advance({ chatTitle: usable });
    }

    case 'research.completed': {
      // Name and flag are read together - a name without the flag must not be
      // reported as generated, and vice versa.
      const generatedName =
        typeof payload.chat_name === 'string' &&
        payload.chat_name &&
        payload.chat_name !== 'Orchestrator (Placeholder)'
          ? payload.chat_name
          : null;

      // The event carries the typed payload - build the envelope here, with no
      // blob round-trip on the critical path.
      const response = toResearchResponse(payload, {
        messageUuid: action.messageUuid,
        correlationId: action.correlationId,
        explicitIntent: action.explicitIntent || undefined,
      });

      return {
        ...state,
        phase: 'completed',
        done: true,
        stages: closeStages(state.stages),
        response,
        intent: response.intent,
        intentSource: response.meta.intentSource,
        verification: response.meta.verification ?? null,
        draftReview: response.meta.draftReview ?? null,
        chatTitle: generatedName ?? state.chatTitle,
        chatTitleGenerated: !!generatedName && payload.chat_name_generated === true,
        // `completed` carries every interim line actually emitted, so a
        // client that connected late still renders the full set.
        interim: reconcileInterim(state.interim, interimFromBlob(payload)),
        // The blob is authoritative for a client that connected late; a blob
        // without cues leaves the live ones alone.
        cues: cuesFromBlob(payload).length ? cuesFromBlob(payload) : state.cues,
      };
    }

    case 'research.failed': {
      const error = String(
        toErrorText(data?.error) || toErrorText(payload.error) || 'Research processing failed. Please try again.'
      );
      return {
        ...state,
        phase: 'failed',
        done: true,
        error,
        stages: failStages(state.stages, error),
      };
    }

    default:
      // Any other `research.*` stage the engine adds renders on its own.
      return key ? advance() : state;
  }
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

export interface UseResearchRunOptions {
  /** Chat group the query belongs to - needed for the reconcile. */
  chatUuid?: string;
  messageUuid?: string;
  /**
   * The version this run is producing, when it is a rerun/edit/refine.
   *
   * Forwarded to the reconcile so it settles THIS version rather than
   * whichever one happens to be current when the request lands.
   */
  versionUuid?: string;
  versionNumber?: number;
  explicitIntent?: ResearchIntent | null;
  /**
   * The chat already has a title, so this run must NOT write one.
   *
   * The engine generates `chat_title` on every turn; forwarding it every time
   * renamed the chat under the user on each follow-up - and would silently
   * undo a manual rename. Only the first turn of an untitled chat names it.
   */
  hasTitle?: boolean;
  /**
   * The signed-in user's id.
   *
   * Required before connecting: the hub negotiates per user and refuses to
   * even try without one. See the connect effect.
   */
  userId?: string | number | null;
  /** Already-translated copy. */
  timeoutMessage?: string;
  connectErrorMessage?: string;
}

export interface UseResearchRunResult extends ResearchRunState {
  reset: () => void;
}

export function useResearchRun(
  correlationId: string | null,
  options: UseResearchRunOptions = {}
): UseResearchRunResult {
  const dispatch = useAppDispatch();
  const [state, rawDispatch] = useReducer(runReducer, INITIAL);

  // Latest options without re-subscribing on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // The reconcile must fire exactly once per run.
  const settledRef = useRef<string | null>(null);

  const reset = useCallback(() => rawDispatch({ type: 'reset' }), []);

  /* ── subscribe, with connect retry ──────────────────────────── */
  useEffect(() => {
    if (!correlationId) return;
    // Stamped with the run it belongs to, so a consumer can reject state
    // left over from the previous one.
    rawDispatch({ type: 'reset', correlationId });
    rawDispatch({ type: 'connecting' });
    settledRef.current = null;

    let cancelled = false;
    let attached: ((data: any, eventName: string) => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const service = getMultiHubSocketService();

    const handleEvent = (data: any, eventName: string) => {
      // Wildcard listener: this filter keeps a concurrent query's events out.
      const eventCorrelationId = data?.correlation_id || data?.correlationId;
      if (eventCorrelationId !== correlationId) return;
      if (!eventName?.startsWith('research.')) return;

      /*
        WHAT IS ACTUALLY ON THE WIRE.

        The loader's cues ride `research.interim` with `stage:
        "progress_cues"`, and when they do not show up there is no way to
        tell from the UI whether the engine sent them, sent them in a shape
        this client does not read, or never sent them at all. Keys and counts
        only - the phrases are derived from the user's question and are not
        log material. Dev builds only; `logger` is silent in production.
      */
      if (isInterimEvent(eventName)) {
        const p = data?.data || {};
        logger.debug('[research] interim event', {
          stage: p.stage ?? null,
          sequence: p.sequence ?? null,
          hasText: !!String(p.text ?? p.message ?? '').trim(),
          cueCount: Array.isArray(p.cues) ? p.cues.length : null,
          keys: Object.keys(p),
        });
      }

      rawDispatch({
        type: 'event',
        eventName,
        data,
        explicitIntent: optionsRef.current.explicitIntent ?? null,
        messageUuid: optionsRef.current.messageUuid,
        correlationId,
        now: Date.now(),
      });
    };

    const attach = () => {
      // Idempotent: never leave two handlers on the hub for one run.
      if (attached) service.off('ai_processing', '*', attached);
      attached = handleEvent;
      service.on('ai_processing', '*', attached);
    };

    /**
     * ATTACH BEFORE CONNECTING - and this ordering is the whole fix.
     *
     * The hub is fire-and-forget: an event published while no handler is on
     * the emitter is GONE. There is no backlog, no replay, no `since` cursor.
     *
     * Attaching after `await connectToHub` left a window at least one network
     * round trip wide (longer on the retry ladder) with nothing listening. On
     * a first send that window is harmless - the engine takes tens of seconds.
     * On a REGENERATE the pipeline is warm: same question, intent already
     * cached, same scope - so `research.started`, `research.intent` and the
     * first `research.interim` can all publish within a second or two of the
     * 202, and land in the gap. The turn then renders with no skeleton and no
     * intent-derived timeout, and if `research.completed` is what was missed,
     * `confirm-completion` is never POSTed: the version stays `processing`,
     * the summary is never written, and the answer only appears on a later
     * refetch.
     *
     * `attach()` is idempotent, so registering early costs nothing. On a
     * regenerate the hub socket is ALREADY OPEN - the first turn of the chat
     * opened it - so this registration takes effect immediately and the window
     * closes entirely. On a cold mount `service.on` is a no-op until a socket
     * exists (`MultiHubSocketService.on` returns early), which is exactly why
     * `attach()` is called again after the connect resolves rather than
     * instead of here: the two cover each other, and neither is redundant.
     */
    attach();

    const connect = async (attempt: number) => {
      try {
        /**
         * IDENTITY FIRST.
         *
         * `negotiate()` returns null immediately when the service has no
         * `currentUserId` - no request is even made - so the socket never
         * connects, the client never joins its user group, and the engine's
         * publish comes back `Not Found`. Every other consumer of this hub
         * sets the id; research did not, and inherited one only by accident
         * when notifications happened to connect first.
         */
        const userId = optionsRef.current.userId;
        if (userId) service.setCurrentUserId(String(userId));

        // `connectToHub` RESOLVES FALSE on failure - it does not throw. The
        // old code awaited it and attached regardless, so a failed connect
        // looked like success: the handler went onto a dead hub and the retry
        // below was unreachable.
        const connected = await service.connectToHub('ai_processing');
        if (cancelled) return;
        if (!connected) throw new Error('connect failed');
        // The cold-mount half of the pair: the pre-connect `attach()` above
        // was a no-op while there was no socket, and a reconnect can drop the
        // handlers with the old one. Idempotent, so this never doubles up.
        attach();
      } catch {
        if (cancelled) return;
        const delay = CONNECT_RETRY_MS[attempt];
        if (delay === undefined) {
          rawDispatch({
            type: 'connect-failed',
            message:
              optionsRef.current.connectErrorMessage ||
              'Lost connection to the research service. The answer may still be processing - reopen the chat to check.',
          });
          return;
        }
        retryTimer = setTimeout(() => connect(attempt + 1), delay);
      }
    };

    connect(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (attached) service.off('ai_processing', '*', attached);
    };
  }, [correlationId]);

  /* ── per-intent timeout ─────────────────────────────────────── */
  useEffect(() => {
    if (!correlationId || state.done) return;
    const budget = timeoutForIntent(state.intent);
    const timer = setTimeout(() => {
      rawDispatch({
        type: 'timeout',
        message:
          optionsRef.current.timeoutMessage ||
          'The research request timed out. The AI may still be processing - please try refreshing the chat.',
      });
    }, budget);
    return () => clearTimeout(timer);
    // Re-armed once the intent lands, so the budget matches the work.
  }, [correlationId, state.intent, state.done]);

  /* ── completion side-effects ────────────────────────────────── */
  useEffect(() => {
    if (state.phase !== 'completed' || !state.response) return;
    const { chatUuid, messageUuid } = optionsRef.current;
    if (!chatUuid || !messageUuid) return;
    if (settledRef.current === correlationId) return;
    settledRef.current = correlationId;

    const raw: any = (state.response.meta.raw as any) || {};

    // Background only - the answer already rendered from the event payload.
    // This is just the retrieval block the citation panel reads.
    dispatch(researchGetMessageContent(chatUuid, messageUuid))
      .then((apiResponse: any) => {
        const blobData = apiResponse?.content ?? apiResponse;
        if (blobData?.retrieval && typeof blobData.retrieval === 'object') {
          rawDispatch({ type: 'retrieval-data', retrieval: blobData.retrieval });
        }
      })
      .catch(() => { /* retrieval data is optional for the citation panel */ });

    // Reconciliation: a FALLBACK writer of the message metadata. The engine
    // writes its own bookkeeping - including the chat's title - directly at
    // the end of the turn, so this call no longer decides whether the chat
    // gets named; it fills in anything the engine's write left open.
    // The title is still forwarded once, by the turn that first names the chat.
    const namesTheChat = !optionsRef.current.hasTitle;
    dispatch(researchConfirmCompletion(chatUuid, messageUuid, {
      chat_name: namesTheChat ? state.chatTitle || undefined : undefined,
      chat_name_generated: namesTheChat
        ? state.chatTitleGenerated || undefined
        : undefined,
      citation_count: state.response.citations.length,
      processing_time_ms: raw.processing_time_ms,
      summary: raw.summary,
      blob_path: raw.blob_path,
      prompt_tokens: raw.prompt_tokens,
      completion_tokens: raw.completion_tokens,
      total_tokens: raw.total_tokens,
      model_used: raw.model_used,
      // Present only on a rerun/edit/refine - names the version this run
      // produced so a later rerun cannot have this completion written onto it.
      ...(optionsRef.current.versionUuid
        ? { version_uuid: optionsRef.current.versionUuid }
        : {}),
      ...(typeof optionsRef.current.versionNumber === 'number'
        ? { version_number: optionsRef.current.versionNumber }
        : {}),
    }))
      // FINALLY, not THEN. The refetch is what puts this chat in the sidebar
      // with the title the server just wrote, and the server now writes that
      // title itself at the end of the turn (research_bookkeeping.name_chat).
      // Chaining it onto a successful reconcile meant a failed or rejected
      // confirm-completion also swallowed the refresh, leaving a stale row.
      .catch(() => { /* reconcile is a fallback; the engine already wrote it */ })
      .finally(() => {
        dispatch(researchListChatGroups()).catch(() => { /* keep the window */ });
      });
  }, [
    state.phase,
    state.response,
    state.chatTitle,
    state.chatTitleGenerated,
    correlationId,
    dispatch,
  ]);

  return useMemo(() => ({ ...state, reset }), [state, reset]);
}

export default useResearchRun;
